/* eslint-disable prettier/prettier */
import { apiService } from './api.service';
import {
	Assessment,
	CreateAssessmentPayload,
	AssessmentSubmission,
	SubmitAssessmentRequest,
	ReviewSubmissionRequest,
	AssessmentStatus,
} from '../types/assessment';
import { error } from 'console';

class AssessmentService {
	/* ------------------ FETCH ------------------ */

	async getAssessmentById(assessmentId: string) {
		const response = await apiService.get<Assessment>(
			`assessments/by-id/${assessmentId}`
		);

		if (!response.success) {
			throw new Error(response.message || 'Assessment not found');
		}

		return response.data;
	}

	async getAssessmentByCourse(courseId: string) {
		console.log('[getAssessmentByCourse] called with courseId:', courseId);

		try {
			const response = await apiService.get<any[]>(
				`assessment/course/${courseId}`
			);

			console.log('[getAssessmentByCourse] raw API response:', response);

			if (!response.success) {
				console.error(
					'[getAssessmentByCourse] API returned success=false',
					{
						message: response.message,
						data: response.data,
					}
				);

				throw new Error(
					response.message || 'No assessment for this course'
				);
			}

			console.log(
				'[getAssessmentByCourse] success, assessments:',
				response.data
			);

			return response.data;
		} catch (error: any) {
			console.error('[getAssessmentByCourse] request failed', {
				courseId,
				error,
				response: error?.response,
				responseData: error?.response?.data,
			});

			// Re-throw so hook can catch it
			throw error;
		}
	}

	async getAssessmentByLesson(lessonId: string) {
		const response = await apiService.get<Assessment>(
			`assessments/by-lesson/${lessonId}`
		);

		if (!response.success) {
			throw new Error(
				response.message || 'No assessment for this lesson'
			);
		}

		return response.data;
	}

	/* ------------------ CREATE / UPDATE ------------------ */

	async createAssessment(payload: CreateAssessmentPayload) {
		return apiService.post<string>('/assessment', {
			...payload,

			// 🔥 Required backend defaults
			assessmenttype: 'assignment', // defaulted
			passinggrade: 70, // required by Joi
			questionsdata: {}, // required by Joi
		});
	}

	async updateAssessment(
		assessmentId: string,
		data: Partial<CreateAssessmentPayload>
	) {
		const response = await apiService.put<Assessment>(
			`assessment/${assessmentId}`,
			data
		);

		if (!response.success) {
			throw new Error(response.message || 'Failed to update assessment');
		}

		return response.data;
	}

	async deleteAssessment(assessmentId: string) {
		const response = await apiService.delete<void>(
			`assessment/${assessmentId}`
		);

		if (!response.success) {
			throw new Error(response.message || 'Failed to delete assessment');
		}
	}

	/* ------------------ SUBMISSION ------------------ */

	async submitAssessment(data: SubmitAssessmentRequest) {
		const response = await apiService.post<AssessmentSubmission>(
			`assessments/${data.assessmentid}/submit`,
			data
		);

		if (!response.success) {
			throw new Error(response.message || 'Submission failed');
		}

		return response.data;
	}

	async getUserSubmissions(assessmentId: string) {
		const response = await apiService.get<AssessmentSubmission[]>(
			`assessments/${assessmentId}/usersubmissions`
		);

		if (!response.success) {
			throw new Error(response.message || 'Failed to fetch submissions');
		}

		return response.data;
	}

	// 🔹 Overview (dashboard-style)
	async getAssessmentOverview() {
		const response = await apiService.get<AssessmentStatus[]>(
			'assessment/status/overview'
		);

		if (!response.success) {
			throw new Error(response.message || 'Failed to fetch overview');
		}

		return response.data;
	}

	// 🔹 Status for ONE assessment
	async getAssessmentStatus(assessmentId: string) {
		const response = await apiService.get<AssessmentStatus>(
			`assessment/${assessmentId}/status`
		);

		console.log('assessment status', response);

		if (!response.success) {
			throw new Error(
				response.message || 'Failed to fetch assessment status'
			);
		}
		console.log('assessment status', response);
		return response.data;
	}

	// 🔹 Can user submit?
	async canSubmitAssessment(assessmentId: string) {
		const response = await apiService.get<{
			canSubmit: boolean;
			reason?: string;
		}>(`assessment/${assessmentId}/cansubmit`);

		if (!response.success) {
			throw new Error(
				response.message || 'Failed to check submit permission'
			);
		}

		return response.data;
	}

	// 🔹 All assessments status for a course
	async getCourseAssessmentsStatus(courseId: string) {
		const response = await apiService.get<AssessmentStatus[]>(
			`assessment/courses/${courseId}/assessments/status`
		);

		if (!response.success) {
			throw new Error(
				response.message || 'Failed to fetch course assessments'
			);
		}

		return response.data;
	}

	// 🔹 All assessments across courses
	async getAllAssessmentsStatus(filters?: {
		status?: string;
		courseId?: string;
		includeGraded?: boolean;
	}) {
		const response = await apiService.get<AssessmentStatus[]>(
			'assessment/status/all',
			filters
		);

		if (!response.success) {
			throw new Error(response.message || 'Failed to fetch assessments');
		}

		return response.data;
	}
}

export const assessmentService = new AssessmentService();
